import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Check, Loader2, PackageOpen, RefreshCw, Settings, X } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import {
  IntakeDto,
  IntakeStatus,
  intakesApi,
} from '../../services/api';
import { Dialog } from '../../components/Dialog';

const STATUS_META: Record<number, { label: string; cls: string }> = {
  [IntakeStatus.Draft]: { label: 'Borrador', cls: 'bg-slate-100 text-slate-600' },
  [IntakeStatus.Declared]: { label: 'Declarada', cls: 'bg-amber-100 text-amber-700' },
  [IntakeStatus.Confirmed]: { label: 'Confirmada', cls: 'bg-green-100 text-green-700' },
  [IntakeStatus.Closed]: { label: 'Cerrada', cls: 'bg-slate-200 text-slate-700' },
};

export const IntakesPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<IntakeStatus | 'all'>(
    IntakeStatus.Declared,
  );
  const [intakes, setIntakes] = useState<IntakeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirming, setConfirming] = useState<IntakeDto | null>(null);

  useEffect(() => {
    setPageConfig({
      title: 'Partidas',
      description: 'Entradas de mercancía de los agricultores',
      actions: [
        {
          label: 'Ajustes',
          icon: Settings,
          onClick: () => navigate('/partidas/ajustes'),
          variant: 'secondary' as const,
          iconOnly: true,
          tooltip: 'Configuración de impresión',
        },
        {
          label: 'Refrescar',
          icon: RefreshCw,
          onClick: () => setRefreshKey((k) => k + 1),
          variant: 'secondary' as const,
          iconOnly: true,
          tooltip: 'Recargar listado',
        },
      ],
    });
  }, [setPageConfig, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    intakesApi
      .list({
        status: statusFilter === 'all' ? undefined : statusFilter,
        take: 100,
      })
      .then((data) => {
        if (!cancelled) setIntakes(data);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) alert('Error al cargar partidas: ' + err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, refreshKey]);

  const totals = useMemo(() => {
    const declared = intakes.filter((i) => i.status === IntakeStatus.Declared).length;
    const confirmed = intakes.filter((i) => i.status === IntakeStatus.Confirmed).length;
    return { declared, confirmed };
  }, [intakes]);

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(
          [
            { value: IntakeStatus.Declared, label: `Pendientes${totals.declared ? ` (${totals.declared})` : ''}` },
            { value: IntakeStatus.Confirmed, label: 'Confirmadas' },
            { value: IntakeStatus.Closed, label: 'Cerradas' },
            { value: 'all' as const, label: 'Todas' },
          ]
        ).map((f) => (
          <button
            key={String(f.value)}
            type="button"
            onClick={() => setStatusFilter(f.value)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              statusFilter === f.value
                ? 'bg-green-600 text-white shadow-sm'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="animate-spin text-slate-400" size={32} />
        </div>
      ) : intakes.length === 0 ? (
        <div className="text-center p-8 bg-slate-50 rounded-lg border border-slate-200 border-dashed">
          <PackageOpen className="mx-auto mb-3 text-slate-400" size={48} />
          <p className="text-slate-500">No hay partidas con este filtro</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Agricultor</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Producto</th>
                <th className="text-center px-4 py-3 text-sm font-semibold text-slate-700">Cajas</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Estimado</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Confirmado</th>
                <th className="text-center px-4 py-3 text-sm font-semibold text-slate-700">Estado</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Fecha</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {intakes.map((intake) => (
                <IntakeRow
                  key={intake.id}
                  intake={intake}
                  onConfirm={() => setConfirming(intake)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        isOpen={!!confirming}
        onClose={() => setConfirming(null)}
        title={confirming ? `Confirmar partida — ${confirming.farmerName}` : ''}
        size="sm"
      >
        {confirming && (
          <ConfirmIntakeForm
            intake={confirming}
            onCancel={() => setConfirming(null)}
            onConfirmed={(updated) => {
              setIntakes((prev) =>
                prev.map((i) => (i.id === updated.id ? updated : i)),
              );
              setConfirming(null);
              setRefreshKey((k) => k + 1);
            }}
          />
        )}
      </Dialog>
    </div>
  );
};

interface IntakeRowProps {
  intake: IntakeDto;
  onConfirm: () => void;
}

const IntakeRow: React.FC<IntakeRowProps> = ({ intake, onConfirm }) => {
  const status = STATUS_META[intake.status] ?? { label: '—', cls: '' };
  return (
    <tr className="hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3 font-medium text-slate-900">{intake.farmerName}</td>
      <td className="px-4 py-3 text-slate-700">
        {intake.productName}
        <span className="ml-2 text-xs text-slate-400">
          {intake.packagingContainerName}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-slate-700">{intake.boxCount}</td>
      <td className="px-4 py-3 text-right font-mono text-sm text-slate-700">
        {formatKg(intake.estimatedKg)} kg
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm text-slate-900">
        {intake.confirmedKg != null ? `${formatKg(intake.confirmedKg)} kg` : '—'}
      </td>
      <td className="px-4 py-3 text-center">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${status.cls}`}
        >
          {status.label}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {new Date(intake.declaredAt).toLocaleString('es-ES', {
          dateStyle: 'short',
          timeStyle: 'short',
        })}
      </td>
      <td className="px-4 py-3 text-right">
        {intake.status === IntakeStatus.Declared ? (
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
          >
            <Check size={14} />
            Confirmar
          </button>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
    </tr>
  );
};

interface ConfirmIntakeFormProps {
  intake: IntakeDto;
  onCancel: () => void;
  onConfirmed: (updated: IntakeDto) => void;
}

const ConfirmIntakeForm: React.FC<ConfirmIntakeFormProps> = ({
  intake,
  onCancel,
  onConfirmed,
}) => {
  const [kg, setKg] = useState(String(intake.estimatedKg));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const value = Number(kg);
    if (!(value > 0)) {
      setError('Introduce un peso válido');
      return;
    }
    try {
      setSubmitting(true);
      const updated = await intakesApi.confirm(intake.id, value);
      onConfirmed(updated);
    } catch (err: any) {
      setError(err.message || 'Error al confirmar');
    } finally {
      setSubmitting(false);
    }
  }

  const diff = Number(kg) - intake.estimatedKg;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <div>
          <strong>{intake.productName}</strong> · {intake.boxCount} cajas
        </div>
        <div className="text-xs text-slate-500">
          Estimado: {formatKg(intake.estimatedKg)} kg
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Kg reales tras pesaje
        </label>
        <input
          type="number"
          step={0.1}
          min={0}
          required
          autoFocus
          value={kg}
          onChange={(e) => setKg(e.target.value)}
          className="w-full px-3 py-2.5 text-lg font-mono border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
        {!isNaN(diff) && Number(kg) > 0 && (
          <p
            className={`mt-1 text-xs ${
              Math.abs(diff) < 0.1
                ? 'text-slate-500'
                : diff > 0
                  ? 'text-green-700'
                  : 'text-amber-700'
            }`}
          >
            {diff > 0 ? '+' : ''}
            {formatKg(diff)} kg vs estimado
          </p>
        )}
      </div>

      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 flex items-center gap-1">
          <X size={14} /> {error}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg font-medium"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Check size={16} />
          )}
          Confirmar partida
        </button>
      </div>
    </form>
  );
};

function formatKg(value: number): string {
  return value.toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
